import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Shield, Plane, Globe2, Package, Truck } from "lucide-react";
import Bolt from "@/components/Bolt";
import LogisticsAnimation from "@/components/LogisticsAnimation";
import { useT } from "@/lib/i18n";

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/c2604f62-1402-40df-b2da-e4ab3cce9288/images/f4ce4c80a44e2b2427f83cb1619c3a3c3ae9e18318770c13abc695bc65f9ff62.png";

export default function Hero() {
  const { t } = useT();

  const badges = [
    { icon: Shield, key: "hero.badge.customs" },
    { icon: Package, key: "hero.badge.ddp" },
    { icon: Plane, key: "hero.badge.fba" },
    { icon: Truck, key: "hero.badge.fairs" },
    { icon: Globe2, key: "hero.badge.gateway" },
  ];

  return (
    <section
      data-testid="hero-section"
      className="relative pt-32 pb-24 sm:pt-40 sm:pb-32 overflow-hidden"
    >
      {/* Soft top illustration band */}
      <img
        src={HERO_BG}
        alt=""
        aria-hidden
        className="absolute inset-0 w-full h-full object-cover opacity-10"
      />
      <div className="zp-hero-mask" />
      <div className="absolute inset-0 zp-grid-bg opacity-40" />
      <LogisticsAnimation variant="all" />
      <Bolt
        size={520}
        color="#5CE1E6"
        className="absolute -left-24 top-16 pointer-events-none"
        style={{ opacity: 0.10, transform: "rotate(-14deg)" }}
      />
      <Bolt
        size={300}
        color="#0A2540"
        className="absolute right-[-40px] bottom-[-50px] pointer-events-none hidden md:block"
        style={{ opacity: 0.05, transform: "rotate(8deg)" }}
      />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div data-testid="hero-eyebrow" className="zp-pill">
              <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] animate-pulse" />
              <span>{t("hero.eyebrow")}</span>
            </div>

            <h1
              data-testid="hero-headline"
              className="mt-7 font-display font-bold text-4xl sm:text-6xl lg:text-7xl tracking-tighter leading-[1.02]"
            >
              {t("hero.title.1")}{" "}
              <span className="relative inline-block">
                <span className="text-[color:var(--zp-navy)]">{t("hero.title.2")}</span>
                <br className="hidden sm:block" />
                <span className="zp-cyan-text inline-flex items-center gap-3">
                  <Bolt size={48} color="#2EC6CC" className="inline-block -mt-1 hidden sm:inline-block" />
                  {t("hero.title.3")}
                </span>
              </span>
            </h1>

            <p
              data-testid="hero-subheadline"
              className="mt-7 text-lg text-[color:var(--zp-text-2)] max-w-2xl leading-relaxed"
            >
              {t("hero.subtitle")}
            </p>

            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Link to="/quote" data-testid="hero-cta-quote" className="zp-btn-primary">
                {t("common.getQuote")} <ArrowUpRight size={16} />
              </Link>
              <Link to="/contact" data-testid="hero-cta-contact" className="zp-btn-ghost">
                {t("common.talkExperts")}
              </Link>
            </div>

            <div className="mt-10 flex flex-wrap gap-2.5">
              {badges.map(({ icon: Icon, key }) => (
                <div
                  key={key}
                  data-testid={`hero-badge-${key.split(".").pop()}`}
                  className="zp-pill"
                >
                  <Icon size={13} className="text-[color:var(--zp-cyan-3)]" />
                  <span className="normal-case tracking-normal text-[12px] text-[color:var(--zp-navy)] font-medium">
                    {t(key)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <HeroPanel />
          </div>
        </div>
      </div>
    </section>
  );
}

function HeroPanel() {
  const { t } = useT();
  return (
    <div data-testid="hero-tracking-panel" className="relative">
      <div className="absolute -inset-6 bg-gradient-to-br from-[#5CE1E6]/30 via-transparent to-transparent blur-3xl pointer-events-none" />
      <div className="relative zp-card p-6 sm:p-7">
        <div className="flex items-center justify-between">
          <div>
            <div className="zp-label">{t("hero.panel.label")}</div>
            <div className="font-display text-lg tracking-tight mt-1">
              {t("hero.panel.shipment")}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="relative inline-flex w-2.5 h-2.5">
              <span className="absolute inset-0 rounded-full bg-emerald-400 opacity-60 animate-ping" />
              <span className="relative w-2.5 h-2.5 rounded-full bg-emerald-500" />
            </span>
            <span className="text-xs text-emerald-600 font-semibold">{t("hero.panel.transit")}</span>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-3 gap-2">
          <Step label={t("hero.panel.origin")} value="MAA" active />
          <Step label={t("hero.panel.transitLabel")} value="DXB" active />
          <Step label={t("hero.panel.destination")} value="FRA" />
        </div>

        <div className="relative mt-4 h-2 rounded-full bg-[color:var(--zp-bg-2)] overflow-hidden">
          <div className="absolute inset-y-0 left-0 w-2/3 bg-gradient-to-r from-[#2EC6CC] to-[#5CE1E6]" />
        </div>

        <div className="zp-divider my-6" />

        <div className="grid grid-cols-2 gap-4">
          <Metric label={t("hero.panel.eta")} value="32h" />
          <Metric label={t("hero.panel.customs")} value={t("hero.panel.cleared")} accent />
          <Metric label={t("hero.panel.mode")} value="Air · DDP" />
          <Metric label={t("hero.panel.pieces")} value="148 / 148" />
        </div>

        <div className="mt-6 flex items-center justify-between text-xs">
          <span className="text-[color:var(--zp-text-3)]">{t("hero.panel.compliance")}</span>
          <span className="zp-gold font-bold">A+ · 99.4%</span>
        </div>
      </div>
    </div>
  );
}

function Step({ label, value, active }) {
  return (
    <div
      className={`rounded-xl border p-3 ${
        active
          ? "border-[color:var(--zp-cyan-2)] bg-[color:var(--zp-cyan-soft)]/60"
          : "border-[color:var(--zp-border)] bg-white"
      }`}
    >
      <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--zp-text-3)]">{label}</div>
      <div className="text-[color:var(--zp-navy)] font-display text-base mt-1">{value}</div>
    </div>
  );
}

function Metric({ label, value, accent }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--zp-text-3)]">{label}</div>
      <div className={`mt-1 font-display text-lg ${accent ? "text-emerald-600" : "text-[color:var(--zp-navy)]"}`}>{value}</div>
    </div>
  );
}
